import MarketingConsent from "components/common/MarketingConsent";
import { useUserContext } from "lib/contexts/UserProvider";
import { GhostUserType } from "lib/types/ghost";
import { getAuthCookies } from "lib/utils/auth";
import { eventTypes } from "lib/utils/constants";
import { publishPostMessage } from "lib/utils/helpers";
import { useCallback, useEffect, useMemo, useRef } from "react";

export type ThirdPartyData = {
  thirdParty: "webengage" | "clevertap" | "custom" | "moengage";
  type: "email" | "phone" | "none";
};

type UserState = GhostUserType & { isLoaded: boolean };

export const useThirdPartyIntegration = () => {
  // Replace useState with useRef since we don't need re-renders when this data changes
  const userRef = useRef<UserState>({ isLoaded: false });
  const {
    state: { user, isAccountResponseLoading },
  } = useUserContext();

  // Memoize the fetch function to avoid recreating it on every render
  const fetchUserData = useCallback(async () => {
    const cookies = getAuthCookies();
    if (!cookies) {
      return null;
    }

    try {
      return {
        accountId: user?.uid,
        email: user?.email,
        phone: user?.phone,
        firstName: user?.metadata?.first_name,
        lastName: user?.metadata?.last_name,
        isLoaded: true,
        marketingConsent: user?.marketing_consent,
      };
    } catch (error) {
      console.error("error", error);
      return null;
    }
  }, [user?.uid]);

  // Memoize the publish function to avoid recreating it
  const publishThirdPartyData = useCallback((thirdPartyData: ThirdPartyData, userData: UserState) => {
    switch (thirdPartyData.thirdParty) {
      case "webengage":
        if (
          thirdPartyData.type === "email" ||
          thirdPartyData.type === "phone" ||
          thirdPartyData.type === "none"
        ) {
          publishPostMessage(eventTypes.SET_WEBENGAGE_DATA, {
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            email: userData?.email,
            phone: userData?.phone,
            type: thirdPartyData.type,
          });
        }
        break;
      case "clevertap":
        publishPostMessage(eventTypes.SET_CLEVERTAP_DATA, {
          name: `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim(),
          email: userData?.email,
          phone: userData?.phone,
          marketingConsent: userData?.marketingConsent,
        });
        break;
      case "moengage":
        publishPostMessage(eventTypes.SET_MOENGAGE_DATA, {
          phone: userData?.phone ?? "",
          type: thirdPartyData.type,
        });
        break;
      case "custom":
        // Backs Shopflo.getUser() on the storefront, which needs the whole
        // identity rather than just an id. uid stays first for the older
        // window.handleFloGetUser callers that only read it.
        publishPostMessage(eventTypes.SET_CUSTOM_USER_DATA, {
          uid: userData?.accountId ?? null,
          email: userData?.email ?? null,
          phone: userData?.phone ?? null,
          isLoggedIn: Boolean(userData?.accountId),
        });
        break;
    }
  }, []);

  const publishLoggedOut = useCallback(() => {
    publishPostMessage(eventTypes.SET_CUSTOM_USER_DATA, {
      uid: null,
      email: null,
      phone: null,
      isLoggedIn: false,
    });
  }, []);

  const sendThirdPartyData = useCallback(
    async ({ thirdParty, type }: ThirdPartyData, user?: UserState) => {
      try {
        const userData = await fetchUserData();
        if (!userData) {
          // No cookies at all, so the shopper is definitively logged out. The
          // storefront awaits a reply before it can act, and silence costs it
          // a full timeout. Only "custom" is request/response - webengage and
          // clevertap are pushes with nobody waiting.
          if (thirdParty === "custom") {
            publishLoggedOut();
          }
          return;
        }

        // Cookies exist but the account is not resolved. Claiming logged out
        // here would send the storefront off to open a login modal at someone
        // who is already signed in, so answer only once the request settles -
        // until then stay quiet and let it ask again.
        if (thirdParty === "custom" && !userData.accountId) {
          if (!isAccountResponseLoading) {
            publishLoggedOut();
          }
          return;
        }

        userRef.current = userData;
        publishThirdPartyData({ thirdParty, type }, user ? user : userData);
      } catch (error) {
        console.error("Error sending third party data:", error);
        if (thirdParty === "custom") {
          publishLoggedOut();
        }
      }
    },
    [fetchUserData, publishThirdPartyData, publishLoggedOut, isAccountResponseLoading],
  );

  // Memoize the return object to prevent unnecessary re-renders of consuming components
  return useMemo(
    () => ({
      user: userRef.current,
      sendThirdPartyData,
      userData: user,
      isAccountResponseLoading,
    }),
    [sendThirdPartyData, user],
  );
};
